import React, { useContext, useEffect, useState } from "react";
import TadobaContext from "../Context/TadobaContext";
import { useParams, useNavigate } from "react-router-dom";
import { makeRequest } from "../../api";
import { IoChevronBack } from "react-icons/io5";
import IdentifiedContainer from "../Upload/IdentifiedContainer";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { PiFolderSimpleBold } from "react-icons/pi"; // Ensure this import exists

const { RangePicker } = DatePicker;

const UnclassifiedTigersGroup = () => {
  const { species_id } = useParams();
  const navigate = useNavigate();
  const api = useContext(TadobaContext);

  const [loading, setLoading] = useState(true);
  const [rangeSelect, setRangeSelect] = useState(true);
  const [ranges, setRanges] = useState([]);
  const [stations, setStations] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedStationID, setSelectedStationID] = useState(null);
  const [showIdentifiedContainer, setShowIdentifiedContainer] = useState(false);
  const [customRangeDates, setCustomRangeDates] = useState(null);
  const [selectedRangeID, setSelectedRangeID] = useState(null);

  useEffect(() => {
    fetchRanges();
  }, []);

  const transformClassificationData = (inputData) => {
    const classified = [
      ...inputData.identified.map((item) => ({
        id: item.image_id,
        image_key: item.image_key,
        species: "Tiger",
        fileName: item.signed_url,
      })),
      ...inputData.unidentified.map((item) => ({
        id: item.image_id,
        image_key: item.image_key,
        species: "Tiger",
        fileName: item.signed_url,
      })),
      ...inputData.unable_to_identify.map((item) => ({
        id: item.image_id,
        image_key: item.image_key,
        species: "Tiger",
        fileName: item.signed_url,
      })),
    ];

    // Group data by species (all are Tiger in this case)
    const grouped_data = {
      Tiger: classified,
    };

    return {
      data: "Successfully Classified",
      classifed: classified,
      unclassified: [], // Empty array as requested
      grouped_data: grouped_data,
      images_uploaded: classified.length,
      time_taken: "na",
      species_found: 1, // Only Tiger in this example
      tiger_images: classified.length,
      success: true,
    };
  };

  const transformIdentifiedTigersData = (inputData) => {
    // Process matched (identified) images
    const matched = {};
    inputData.identified.forEach((item) => {
      matched[item.image_id] = item.tiger_name || item.tiger_name_1;
    });

    // Process unmatched (unidentified) images
    const unmatched = inputData.unidentified.map((item) => item.image_id);

    // Process unidentified (unable_to_identify) images
    const unidentified = inputData.unable_to_identify.map(
      (item) => item.image_id
    );

    return {
      matched: matched,
      unmatched: unmatched,
      unidentified: unidentified,
    };
  };

  const fetchRanges = async (start = "", end = "") => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("start_date", start);
      formData.append("end_date", end);

      const result = await makeRequest(
        "post",
        "api/v1/map/range_list_for_tigers/",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const ranges = result.data.data || [];

      // Sort alphanumerically (by 'name' or change as needed)
      const sortedRanges = ranges.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );

      setRanges(sortedRanges);
    } catch (error) {
      console.error("Error loading ranges", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStations = async (rangeId, rangeName, start, end) => {
    setLoading(true);
    try {
      setSelectedRange(rangeName);
      setSelectedRangeID(rangeId);
      setRangeSelect(false);

      const body = {
        range_ids: [rangeId],
        start_date: start,
        end_date: end,
      };

      const result = await makeRequest(
        "post",
        "api/v1/map/station_list_for_tigers/",
        JSON.stringify(body),
        { headers: { "Content-Type": "application/json" } }
      );

      const stations = result.data.data || [];

      // Sort alphanumerically (by 'name' or change as needed)
      const sortedStations = stations.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );

      setStations(sortedStations);
    } catch (error) {
      console.error("Error loading stations", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTigers = async (stationID) => {
    setLoading(true);
    try {
      let start = "";
      let end = "";

      if (customRangeDates && customRangeDates.length === 2) {
        start = dayjs(customRangeDates[0]).format("YYYY-MM-DD");
        end = dayjs(customRangeDates[1]).format("YYYY-MM-DD");
      }

      const body = {
        station_id: stationID,
        start_date: start,
        end_date: end,
      };

      const result = await makeRequest(
        "post",
        "api/v1/identification/tiger_direct_result/",
        JSON.stringify(body),
        { headers: { "Content-Type": "application/json" } }
      );

      const data = result.data.data || {};
      const classification = transformClassificationData(data);
      const identifiedTigers = transformIdentifiedTigersData(data);
      api.setClassification(classification);
      api.setIdentifiedTigers(identifiedTigers);
    } catch (error) {
      console.error("Error loading stations", error);
    } finally {
      setLoading(false);
      setShowIdentifiedContainer(true);
    }
  };

  const handleStationClick = (station) => {
    setSelectedStationID(station.name);
    fetchTigers(station.id);
  };

  const handleBack = () => {
    if (rangeSelect) {
      navigate(`/images/key/Tigerimages/${species_id}/`);
    } else {
      setRangeSelect(true);
    }
  };

  const handleCustomRangeChange = (dates) => {
    if (!dates || dates.length !== 2) {
      setCustomRangeDates(null);

      if (rangeSelect) {
        fetchRanges("", "");
      } else if (selectedRangeID && selectedRange) {
        fetchStations(selectedRangeID, selectedRange, "", "");
      }
    } else {
      setCustomRangeDates(dates);
    }
  };

  if (showIdentifiedContainer) {
    return (
      <div className="Classify_Container">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "1rem",
            padding: "2vh 0 0 1vw",
          }}
        >
          <IoChevronBack
            style={{ cursor: "pointer", fontSize: "1.5rem" }}
            onClick={() => setShowIdentifiedContainer(false)}
          />
          <span>
            {selectedRange} - {selectedStationID} - Images
          </span>
        </div>
        <IdentifiedContainer />
      </div>
    );
  }

  const renderList = (items, onClickFn) => (
    <div className="image-grid">
      {items
        .filter((item) => item.count > 0)
        .map((item) => (
          <div
            key={item.id}
            className="image-folder"
            onClick={() => onClickFn(item)}
          >
            <PiFolderSimpleBold size={32} />
            <div className="folder-title">{item.name}</div>
            <div className="folder-count">{item.count} images</div>
          </div>
        ))}
    </div>
  );

  return (
    <div className="Setting_User">
      <div className="Setting_User_Header">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "1rem",
            padding: "2vh 0 0 1vw",
          }}
        >
          <IoChevronBack className="unidentified-back" onClick={handleBack} />
          {rangeSelect ? (
            <span>Ranges</span>
          ) : (
            <span>{selectedRange} - Stations</span>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            paddingLeft: "20px",
          }}
        >
          <RangePicker
            onChange={handleCustomRangeChange}
            value={customRangeDates}
            format="DD/MM/YYYY"
          />{" "}
          <button
            onClick={() => {
              const [start, end] = customRangeDates || [];
              if (!start || !end) return;

              const formattedStart = dayjs(start).format("YYYY-MM-DD");
              const formattedEnd = dayjs(end).format("YYYY-MM-DD");

              if (rangeSelect) {
                fetchRanges(formattedStart, formattedEnd);
              } else {
                fetchStations(
                  selectedRangeID,
                  selectedRange,
                  formattedStart,
                  formattedEnd
                );
              }
            }}
            style={{
              padding: "1vh 1vw",
              backgroundColor: "#FF8214",
              color: "#fff",
              border: "none",
              borderRadius: "1rem",
              cursor: "pointer",
            }}
          >
            Apply
          </button>
        </div>
      </div>
      {loading ? (
        <div className="loader-container Images_Table">
          <div className="spinner" />
          <p>Loading data...</p>
        </div>
      ) : (
        <div className="Images_Table scrollable-container">
          {rangeSelect
            ? renderList(ranges, (range) => {
                const [start, end] = customRangeDates || [];
                const formattedStart = start
                  ? dayjs(start).format("YYYY-MM-DD")
                  : "";
                const formattedEnd = end ? dayjs(end).format("YYYY-MM-DD") : "";
                fetchStations(
                  range.id,
                  range.name,
                  formattedStart,
                  formattedEnd
                );
              })
            : renderList(stations, handleStationClick)}
        </div>
      )}
    </div>
  );
};

export default UnclassifiedTigersGroup;
